// The siege window on the game screen (ui-map-interface.md §8 "The time
// cell", §12.1 "The window head" and §8 "The squad chip"; the game critique
// of 2026-10-14, P1 #3). Pure functions: the overlay reads the state and
// the fleet layer, and this module decides which window concerns the
// reader, what each side has near it, and how ready each squad mate is.
//
// **Which window concerns the reader.** A window concerns the reader when
// the reader's faction holds the object (DEFEND), when the faction pledged
// at it or an own hull sails to pledge at it (ATTACK), or when an own hull
// stands inside `near_px` of it (NEAR). Every other window concerns
// nobody, and the head names the next one of them as the window of the
// sea. The Sieges frame lists every object for every reader, so the rule
// reads no field the detection filter withholds.
//
// **What a side has near the target.** Own and allied hulls are the ones
// the fleet layer holds inside `near_px`; a foe is a contact of another
// faction the layer holds there, which is a hull the reader's faction
// hears (the server's detection filter). The pledged count of the own side
// is `SiegeInfo.own_commits`; the foe side is the flag `enemy_pledged`
// alone, never a count (R794).

import { hhmm } from '../locale/clock';
import { leftSpan } from '../locale/span';
import type { ClanMember, SiegeInfo } from '../gen/proto/aquaphobia';
import { SIEGE_WINDOW } from '../gen/presentation';
import { siege as S } from '../gen/strings';
import { cellCenter } from '../world';
import { esc } from './esc';
import type { TimerClaim } from './notices';
import { sitePlace } from './overlay/markup';

/** Why a window concerns the reader, or null when it concerns nobody. */
export type WindowRole = 'defend' | 'attack' | 'near' | null;

/** One hull the fleet layer holds, as the siege rules read it. */
export interface FleetPresence {
  x: number;
  y: number;
  faction: number;
  /** One of the reader's own hulls. */
  own: boolean;
  /** The owning commander's account id, own faction only; 0 = not sent. */
  owner: number;
  /** Own hulls only: the object this hull sails to pledge at; 0 = none. */
  pledgeSite: number;
}

/** One window, with everything the three surfaces draw of it. */
export interface WindowView {
  g: SiegeInfo;
  role: WindowRole;
  /** The window is open now. */
  open: boolean;
  /** World seconds to the open, or to the close while it is open. */
  leftSec: number;
  /** The seconds of the world day at which it opens and closes. */
  openSec: number;
  closeSec: number;
}

/** What `nextWindows` must be told about the reader and the world. */
export interface WindowWorld {
  faction: number;
  /** The length of a window, in world seconds (`rules.objWindowSec`). */
  span: number;
  /** World seconds to the next window of an hour; negative while open. */
  windowIn: (hour: number) => number;
  /** The hulls the fleet layer holds. */
  fleets: readonly FleetPresence[];
}

/** The reach of "near the target", in world px. */
const NEAR_PX = SIEGE_WINDOW.nearPx;

/** True when the hull stands inside `NEAR_PX` of the cell centre. */
function nearCell(f: { x: number; y: number }, cx: number, cy: number): boolean {
  const dx = f.x - cx, dy = f.y - cy;
  return dx * dx + dy * dy <= NEAR_PX * NEAR_PX;
}

/** Why one object's window concerns the reader. */
export function windowRole(g: SiegeInfo, faction: number, fleets: readonly FleetPresence[]): WindowRole {
  if (faction === 0) return null;
  if (g.owner === faction) return 'defend';
  if (g.ownCommits > 0 || g.youCommitted || g.attacker === faction) return 'attack';
  if (fleets.some((f) => f.own && f.pledgeSite === g.siteId)) return 'attack';
  const [cx, cy] = cellCenter(g.cell);
  return fleets.some((f) => f.own && nearCell(f, cx, cy)) ? 'near' : null;
}

/** One window as the three surfaces read it. */
function viewOf(g: SiegeInfo, w: WindowWorld): WindowView {
  const inSec = w.windowIn(g.windowHour);
  const open = inSec < 0;
  return {
    g,
    role: windowRole(g, w.faction, w.fleets),
    open,
    leftSec: open ? Math.max(0, w.span + inSec) : inSec,
    openSec: g.windowHour * 3600,
    closeSec: g.windowHour * 3600 + w.span,
  };
}

/** The order of the windows: an open window first, by its close, then the
 *  windows to come, by their open; the site id breaks a tie. */
function byTime(a: WindowView, b: WindowView): number {
  return Number(b.open) - Number(a.open) || a.leftSec - b.leftSec || a.g.siteId - b.g.siteId;
}

/**
 * The next window that concerns the reader (`mine`), and the next window
 * of the sea (`next`), which the head names when `mine` is null. Both are
 * null with no object on the map or before the hello.
 */
export function nextWindows(list: readonly SiegeInfo[], w: WindowWorld):
{ mine: WindowView | null; next: WindowView | null } {
  if (!(w.span > 0) || list.length === 0) return { mine: null, next: null };
  const all = list.map((g) => viewOf(g, w)).sort(byTime);
  return { mine: all.find((v) => v.role !== null) ?? null, next: all[0] ?? null };
}

/**
 * The window of the time cell (§8): the reader's window while it is open,
 * or while it opens inside `slot_sec` world seconds. A window that opens
 * while the founding truce still holds keeps the truce in the cell, because
 * no weapon fires in that window. An open window of an object the reader's
 * faction holds, with a foe pledged, is a siege warning, which has its own
 * row in the dock (R1753), so it takes no timer.
 */
export function slotWindow(mine: WindowView | null, truceLeftSec: number): WindowView | null {
  if (!mine) return null;
  if (mine.open) {
    const warned = mine.role === 'defend' && (mine.g.enemyPledged || mine.g.contested);
    return warned ? null : mine;
  }
  if (mine.leftSec > SIEGE_WINDOW.slotSec) return null;
  return mine.leftSec >= truceLeftSec ? mine : null;
}

/** The kind and the place of the object: "Throne · Kelp Deep". */
export function targetName(g: SiegeInfo): string {
  return S.target({ kind: g.kindName, site: sitePlace(g) });
}

/** The timer claim of the time cell (§8). The countdown is the span of
 *  the map countdowns, rounded up (R2741, R2756). */
export function windowClaim(v: WindowView): TimerClaim {
  const left = leftSpan(v.leftSec);
  const target = esc(targetName(v.g));
  return {
    fault: false,
    glyph: S.glyph,
    label: S.timer_label,
    short: S.timer_short,
    figure: left,
    tail: v.open ? S.timer_tail_open : S.timer_tail_shut,
    sentence: v.open
      ? S.timer_sentence_open({ target, at: hhmm(v.closeSec), left })
      : S.timer_sentence_shut({ target, at: hhmm(v.openSec), left }),
  };
}

/** The hulls of each side inside `NEAR_PX` of the object. */
export interface SideCounts {
  /** The reader's own hulls. */
  own: number;
  /** The hulls of the reader's faction that are not the reader's. */
  allied: number;
  /** The contacts of every other faction that the reader's faction hears. */
  foes: number;
}

/** Counts each side near one object's cell. */
export function sidesNear(cell: number, faction: number, fleets: readonly FleetPresence[]): SideCounts {
  const [cx, cy] = cellCenter(cell);
  const out: SideCounts = { own: 0, allied: 0, foes: 0 };
  for (const f of fleets) {
    if (!nearCell(f, cx, cy)) continue;
    if (f.own) out.own++;
    else if (f.faction === faction) out.allied++;
    else if (f.faction !== 0) out.foes++;
  }
  return out;
}

/** The role tag of the head: why the window concerns the reader, or who
 *  holds the object when it concerns nobody. */
function roleTag(v: WindowView, factionName: (f: number) => string): string {
  switch (v.role) {
    case 'defend': return S.role_defend;
    case 'attack': return S.role_attack;
    case 'near': return S.role_near;
  }
  return v.g.owner === 0 ? S.role_free : S.role_held({ faction: esc(factionName(v.g.owner)) });
}

/** What the head needs that the module does not read itself. */
export interface HeadEnv {
  faction: number;
  fleets: readonly FleetPresence[];
  factionName: (f: number) => string;
  /** The reader flies in a squad: RALLY stands. */
  squad: boolean;
  /** RALLY rests after a press. */
  rallyResting: boolean;
}

/**
 * The window head of WAR (§12.1): one block above the pills. Line 1 is
 * the target and the countdown, line 2 the role and the two hours on the
 * world clock, line 3 the two sides near the target. SHOW centres the map
 * on the object; RALLY stands for a reader in a squad, on a window that
 * concerns the reader. With no window of the reader the block names the
 * next window of the sea and says how a window becomes the reader's.
 */
export function windowHeadHtml(
  ws: { mine: WindowView | null; next: WindowView | null }, env: HeadEnv,
): string {
  const v = ws.mine ?? ws.next;
  if (!v) {
    return `<section class="wh none" aria-label="${esc(S.head_label)}"><p class="wh-note">${S.none_set}</p></section>`;
  }
  const g = v.g;
  const mine = ws.mine !== null;
  const when = v.open
    ? S.when_open({ close: hhmm(v.closeSec) })
    : S.when_shut({ open: hhmm(v.openSec), close: hhmm(v.closeSec) });
  const sides = sidesNear(g.cell, env.faction, env.fleets);
  const ownNear = sides.own + sides.allied;
  const own = [S.own_near({ n: ownNear })];
  if (g.ownCommits > 0) own.push(S.own_pledged({ n: g.ownCommits }));
  const foes = [S.foes_seen({ n: sides.foes }), g.enemyPledged ? S.foes_pledged : S.foes_none];
  const rally = mine && env.squad
    ? `<button class="ck-b dk wh-rally" data-act="rally" data-id="${g.siteId}" data-n="${g.cell}"`
      + `${env.rallyResting ? ' disabled' : ''} title="${esc(S.rally_title)}">`
      + `<span>${env.rallyResting ? S.rally_sent : S.rally}</span></button>`
    : '';
  return `<section class="wh${mine ? ' mine' : ''}${v.open ? ' open' : ''}" aria-label="${esc(S.head_label)}">`
    + `<div class="wh-l1"><span class="wh-t">${esc(targetName(g))}${g.featured ? featuredTagHtml() : ''}</span>`
    + `<b class="wh-c" title="${esc(v.open ? S.timer_tail_open : S.timer_tail_shut)}">${leftSpan(v.leftSec)}</b></div>`
    + `<div class="wh-l2"><span class="wh-r">${roleTag(v, env.factionName)}</span><span>${when}</span></div>`
    + `<div class="wh-l3"><span><i>${S.side_own}</i> ${own.join(' · ')}</span>`
    + `<span><i>${S.side_foes}</i> ${foes.join(' · ')}</span></div>`
    + (mine ? '' : `<p class="wh-note">${S.none_mine}</p>`)
    + `<div class="wh-acts"><button class="ck-b qt" data-act="focuscell" data-n="${g.cell}"`
    + ` title="${esc(S.show_title)}"><span>${S.show}</span></button>${rally}</div>`
    + '</section>';
}

/** The FEATURED ×2 tag of the featured object of the world day (R3272,
 *  ui-map-interface.md §12.1): after the name, on the window head and on
 *  the object card. Its title says the two doubles in words. */
export function featuredTagHtml(): string {
  return `<span class="ft-tag" title="${esc(S.featured_title)}">${esc(S.featured_tag)}</span>`;
}

// ---------------------------------------------------------------------------
// The squad readiness (§8 "The squad chip")
// ---------------------------------------------------------------------------

/**
 * How ready one squad mate is for the reader's window, from what the
 * client holds: NEAR (a hull of the mate inside `near_px` of the target),
 * SEA (a hull of the mate on the chart), ONLINE, OFFLINE and RESERVE. The
 * client holds a mate's hulls only inside its own area of interest, so SEA
 * means "a hull in view" and never "no hull at sea".
 */
export type Readiness = 'near' | 'sea' | 'online' | 'offline' | 'reserve';

/** The pips of each readiness: three for NEAR down to none. */
const READY_PIPS: Record<Readiness, number> = { near: 3, sea: 2, online: 1, offline: 0, reserve: 0 };

/** The readiness of one member. `self` is the reader's account id. */
export function readinessOf(
  m: ClanMember, self: number, fleets: readonly FleetPresence[], target: number | null,
): Readiness {
  if (m.reserve) return 'reserve';
  const me = m.accountId === self;
  const hulls = fleets.filter((f) => (me ? f.own : !f.own && f.owner === m.accountId));
  if (target !== null) {
    const [cx, cy] = cellCenter(target);
    if (hulls.some((f) => nearCell(f, cx, cy))) return 'near';
  }
  if (!me && !m.online) return 'offline';
  return hulls.length > 0 ? 'sea' : 'online';
}

/** The word of each readiness. */
const READY_WORDS: Readonly<Record<Readiness, string>> = {
  near: S.ready_near, sea: S.ready_sea, online: S.ready_online, offline: S.ready_offline, reserve: S.ready_reserve,
};

/** The word of one readiness. */
export function readinessWord(r: Readiness): string {
  return READY_WORDS[r];
}

/** The pip mark of one readiness: three cells, the held ones filled, so
 *  the state reads by shape and never by ink alone. */
export function pipsHtml(r: Readiness): string {
  const n = READY_PIPS[r];
  return `<span class="rdy r-${r}" aria-hidden="true">${[0, 1, 2].map((i) => `<i${i < n ? ' class="on"' : ''}></i>`).join('')}</span>`;
}

/** The window line of the roster card: the window and its target, or the
 *  sentence that no window concerns the reader. */
export function squadWindowHtml(mine: WindowView | null, rallyResting: boolean): string {
  if (!mine) return `<div class="sqwin none">${S.squad_none}</div>`;
  const at = hhmm(mine.open ? mine.closeSec : mine.openSec);
  return `<div class="sqwin"><span class="sw-t">${mine.open
    ? S.squad_window_open({ at, target: esc(targetName(mine.g)) })
    : S.squad_window({ at, target: esc(targetName(mine.g)) })}</span>`
    + `<b class="sw-c">${leftSpan(mine.leftSec)}</b></div>`
    + `<div class="acts sqrally"><button class="primary" data-act="rally" data-id="${mine.g.siteId}"`
    + ` data-n="${mine.g.cell}"${rallyResting ? ' disabled' : ''} title="${esc(S.rally_title)}">`
    + `${rallyResting ? S.rally_sent : S.rally}</button></div>`;
}
